import React, { useEffect, useState } from 'react';
import { ClipboardCheck, AlertCircle, Smile, Meh, Frown, ChevronDown } from 'lucide-react';
import api from '../../api';

const MOOD_META = {
  good: { icon: Smile, label: 'Confiant', color: '#10b981' },
  neutral: { icon: Meh, label: 'Neutre', color: '#f59e0b' },
  needs_work: { icon: Frown, label: 'À revoir', color: '#f43f5e' },
};

export default function EleveBilansPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    document.title = 'Mes bilans · Élève';
    (async () => {
      try {
        const r = await api.boEleveHistory();
        setItems((r?.items || []).filter(i => i.bilan));
      } catch (_) { /* silent */ }
      setLoading(false);
    })();
  }, []);

  return (
    <div className="max-w-5xl" data-testid="eleve-bilans-page">
      <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-rose-500 mb-2">Comptes-rendus</div>
      <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        Mes bilans
      </h1>
      <p className="text-sm text-white/60 mt-2 mb-6">Chaque leçon est commentée par ton moniteur : ressenti, progression, points à retravailler.</p>

      {loading && <div className="text-white/40 text-sm">Chargement…</div>}

      {!loading && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-8 text-center">
          <ClipboardCheck className="w-8 h-8 text-white/30 mx-auto mb-2" />
          <div className="text-white/60 text-sm">Aucun bilan reçu pour le moment.</div>
          <div className="text-white/40 text-xs mt-1">Ils apparaîtront ici après tes premières leçons effectuées.</div>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2" data-testid="bilans-list">
          {items.map((l) => {
            const b = l.bilan || {};
            const mood = MOOD_META[b.mood] || MOOD_META.neutral;
            const MoodIcon = mood.icon;
            const isOpen = openId === l.id;
            return (
              <div key={l.id} className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
                <button onClick={() => setOpenId(isOpen ? null : l.id)} className="w-full flex items-center gap-3 p-4 hover:bg-white/5 transition">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${mood.color}22`, color: mood.color }}>
                    <MoodIcon size={16} />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-white font-semibold text-sm truncate">
                      {new Date(l.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </div>
                    <div className="text-white/50 text-xs mt-0.5">
                      {l.teacher_name || 'Enseignant'} · {l.duration_min} min · <span style={{ color: mood.color }}>{mood.label}</span>
                    </div>
                  </div>
                  <ChevronDown size={14} className={`text-white/40 transition ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 border-t border-white/5 pt-3 space-y-2 text-sm text-white/75">
                    {b.competences_travaillees?.length > 0 && (
                      <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-1">Compétences travaillées</div>
                        <div className="flex flex-wrap gap-1.5">
                          {b.competences_travaillees.map(c => (
                            <span key={c} className="text-[11px] px-2 py-0.5 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-300">{c}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {b.commentaire && (
                      <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-1">Commentaire du moniteur</div>
                        <div className="text-white/80 leading-relaxed">{b.commentaire}</div>
                      </div>
                    )}
                    {b.progression_note != null && (
                      <div className="flex items-center gap-2">
                        <div className="text-[10px] font-black uppercase tracking-widest text-white/40">Note progression</div>
                        <div className="text-white font-black text-lg">{b.progression_note}<span className="text-white/40 text-sm">/5</span></div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {items.length === 0 && !loading && (
        <div className="mt-6 rounded-lg border border-blue-500/25 bg-blue-500/5 p-3 text-xs text-blue-200/80 flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <div>Les bilans sont rédigés par ton moniteur ECSR après chaque leçon effectuée.</div>
        </div>
      )}
    </div>
  );
}
